/**
 * MEGATOKE — header.js
 * <header-component>: mobile drawer + transparent-to-solid on scroll.
 * Deferred. Uses window.Megatoke (assets/theme.js) when present, degrades without it.
 */

(function () {
  'use strict';

  const utils = (window.Megatoke && window.Megatoke.utils) || {};

  /** Local fallback in case theme.js hasn't parsed yet. */
  const rafThrottle =
    utils.rafThrottle ||
    function (fn) {
      let queued = false;
      return function (...args) {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn.apply(this, args);
        });
      };
    };

  const prefersReducedMotion = function () {
    if (utils.prefersReducedMotion) return utils.prefersReducedMotion();
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-drawer]');
      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.overlay = document.querySelector('[data-drawer-overlay]');

      this.isOpen = false;

      this.onToggleClick = this.onToggleClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOverlayClick = this.close.bind(this);
      this.onScroll = rafThrottle(this.updateScrolled.bind(this));
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onSubmenuToggle = this.onSubmenuToggle.bind(this);

      if (this.toggle && this.drawer) {
        this.toggle.addEventListener('click', this.onToggleClick);
        this.addEventListener('keydown', this.onKeydown);
        this.drawer.addEventListener('click', this.onSubmenuToggle);
        if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
      }

      // Close the drawer if the viewport grows past the point where it applies.
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.desktopQuery.addEventListener('change', this.onBreakpoint);

      if (this.hasAttribute('data-transparent')) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateScrolled();
      }

      this.publishHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(rafThrottle(this.publishHeight.bind(this)));
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.drawer) this.drawer.removeEventListener('click', this.onSubmenuToggle);
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
      window.removeEventListener('scroll', this.onScroll);
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onBreakpoint);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.unlockScroll();
    }

    /** Lets the section below offset itself under a fixed/transparent header. */
    publishHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    updateScrolled() {
      this.classList.toggle('header--scrolled', window.scrollY > 8);
    }

    onBreakpoint(event) {
      if (event.matches && this.isOpen) this.close();
    }

    onToggleClick() {
      if (this.isOpen) this.close();
      else this.open();
    }

    /**
     * Expand/collapse a mobile subcollection menu (event-delegated on the drawer).
     * Both the chevron button AND the parent label toggle it; a parent link that
     * has no submenu is left alone so it navigates normally.
     */
    onSubmenuToggle(event) {
      const trigger = event.target.closest('[data-submenu-toggle], .header__drawer-link');
      if (!trigger || !this.drawer.contains(trigger)) return;

      const item = trigger.closest('.header__drawer-item--has-sub');
      if (!item) return;

      event.preventDefault();

      const willOpen = !item.classList.contains('is-open');
      item.classList.toggle('is-open', willOpen);

      const btn = item.querySelector('[data-submenu-toggle]');
      if (btn) btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

      const submenu = item.querySelector('.header__drawer-submenu');
      if (submenu) {
        if (willOpen) submenu.removeAttribute('inert');
        else submenu.setAttribute('inert', '');
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        this.close();
        this.toggle.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      // Trap focus between the toggle and the drawer's last control.
      const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null && !el.closest('[inert]')
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.toggle)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      this.isOpen = true;
      this.drawer.hidden = false;
      if (this.overlay) this.overlay.hidden = false;

      // Force a frame so the transition runs from the unhidden state.
      requestAnimationFrame(() => {
        this.classList.add('header--drawer-open');
        if (this.overlay) this.overlay.classList.add('is-visible');
      });

      this.toggle.setAttribute('aria-expanded', 'true');
      this.lockScroll();

      const first = this.drawer.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    close() {
      this.isOpen = false;
      this.classList.remove('header--drawer-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.unlockScroll();

      const finish = () => {
        if (this.isOpen) return;
        this.drawer.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
      };

      if (prefersReducedMotion()) {
        finish();
        return;
      }

      this.drawer.addEventListener('transitionend', finish, { once: true });
      // Safety net: transitionend never fires if the transition is skipped.
      setTimeout(finish, 400);
    }

    lockScroll() {
      document.body.classList.add('scroll-locked');
    }

    unlockScroll() {
      document.body.classList.remove('scroll-locked');
    }
  }

  if (!customElements.get('header-component')) {
    customElements.define('header-component', HeaderComponent);
  }
})();
